import { z } from 'zod';
import { type Platform, platformSchema } from './common';

export const POST_STATUSES = ['idea', 'bozza', 'pronto', 'programmato', 'pubblicato', 'scartato'] as const;
export type PostStatus = (typeof POST_STATUSES)[number];

export const POST_STATUS_LABELS: Record<PostStatus, string> = {
  idea: 'Idea',
  bozza: 'Bozza',
  pronto: 'Pronto',
  programmato: 'Programmato',
  pubblicato: 'Pubblicato',
  scartato: 'Scartato',
};

/** Piattaforme dove si pubblicano post (il sito no). */
export const SOCIAL_PLATFORMS: Platform[] = ['instagram', 'facebook', 'google_business', 'tiktok', 'youtube', 'linkedin'];

/** Lunghezza massima del testo per piattaforma. */
export const CAPTION_LIMITS: Partial<Record<Platform, number>> = {
  instagram: 2200,
  facebook: 63206,
  google_business: 1500,
  tiktok: 2200,
  linkedin: 3000,
};

export interface PostMedia {
  id: string;
  kind: 'image' | 'video' | 'file';
  thumbUrl: string | null;
  downloadUrl: string;
}

export interface Post {
  id: string;
  pageId: string;
  channels: Platform[];
  status: PostStatus;
  title: string;
  caption: string;
  hashtags: string;
  mediaIds: string[];
  media: PostMedia[];
  scheduledAt: string | null;
  publishedAt: string | null;
  publishedUrl: string;
  source: 'manuale' | 'ai';
  aiNote: string;
  createdAt: string;
  updatedAt: string;
}

const isoDateTime = z.iso.datetime({ offset: true });

// Campi senza valori predefiniti: in modifica un campo assente deve restare com'è.
const postFields = z.object({
  pageId: z.string().min(1, 'Scegli la scheda'),
  channels: z.array(platformSchema).max(6),
  status: z.enum(POST_STATUSES),
  title: z.string().trim().max(200),
  caption: z.string().max(10000),
  hashtags: z.string().max(2000),
  mediaIds: z.array(z.string()).max(20),
  scheduledAt: isoDateTime.nullable(),
  aiNote: z.string().max(2000),
});

export const postCreateSchema = postFields.partial().extend({
  pageId: z.string().min(1, 'Scegli la scheda'),
  channels: z.array(platformSchema).max(6).default([]),
  status: z.enum(POST_STATUSES).default('bozza'),
  source: z.enum(['manuale', 'ai']).optional(),
});
export type PostCreateInput = z.input<typeof postCreateSchema>;

export const postUpdateSchema = postFields
  .extend({ publishedUrl: z.string().trim().max(500) })
  .partial()
  .refine((v) => Object.keys(v).length > 0, { message: 'Niente da salvare' });
export type PostUpdateInput = z.input<typeof postUpdateSchema>;

export const postListQuerySchema = z.object({
  pageId: z.string().optional(),
  from: isoDateTime.optional(),
  to: isoDateTime.optional(),
  status: z.enum(POST_STATUSES).optional(),
  limit: z.coerce.number().int().min(1).max(500).default(300),
});

export const markPublishedSchema = z.object({ url: z.string().trim().max(500).optional() });

export interface ChannelCadence {
  channelId: string;
  pageId: string;
  platform: Platform;
  goalPerWeek: number;
  publishedLast7: number;
  plannedNext7: number;
  lastPublishedAt: string | null;
  daysSinceLast: number | null;
  /** ok = in regola, late = in ritardo e niente in programma. */
  status: 'ok' | 'late';
}
